import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BookmarksOutlinedIcon from "@mui/icons-material/BookmarksOutlined";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EmailIcon from "@mui/icons-material/Email";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import FileCopy from "@mui/icons-material/FileCopy";
import GroupIcon from "@mui/icons-material/Group";
import InboxIcon from "@mui/icons-material/Inbox";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SearchIcon from "@mui/icons-material/Search";
import SecurityIcon from "@mui/icons-material/Security";import SettingsIcon from "@mui/icons-material/Settings";
import SettingsAccessibility from "@mui/icons-material/SettingsAccessibility";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TimelineIcon from "@mui/icons-material/Timeline";
import TodayIcon from "@mui/icons-material/Today";
import WorkIcon from "@mui/icons-material/Work";
import { calendarRoute } from "calendar/calendar-navigation";
import Collapse from "@mui/material/Collapse";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/system";
import attorneyHubMarketplaceLogo from "_shared/_views/assets/attorney-hub-marketplace-logo.svg";
import { isRouteWithinRoot } from "_shared/_views/route-access-guard";
import useSessionUser from "permissions/session/use-session-user";
import React, { useState } from "react";
import { Link, useLocation } from "react-router";
import { useSession } from "users/__session/session-context";

const NavDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "closeAfterTransition" && prop !== "temporary",
})<{ temporary: boolean }>(({ temporary, theme }) => ({
  width: temporary ? "auto" : "17rem",
  "& .MuiDrawer-paper": {
    paddingTop: theme.spacing(2),
    position: "absolute",
    width: "17rem",
  },
}));
const NavContainer = styled("div")<{ temporary: boolean }>((props) => ({
  height: "100%",
  overflow: "auto",
  marginTop: props.temporary ? props.theme.spacing(6) : props.theme.spacing(8),
  position: "relative",
}));
const MarketplaceLink = styled(Link)((props) => ({
  opacity: 0.6,
  textAlign: "center",
  transition: "opacity 0.5s ease-out",
  "&:hover": {
    opacity: 1,
  },
  "& img": {
    margin: props.theme.spacing(2, "auto"),
    width: "75%",
    [props.theme.breakpoints.down("sm")]: {
      width: "66%",
    },
  },
}));

type SideNavProps = {
  showNav: boolean;
  navMenuClosed: () => void;
};

export default function SideNav(props: Readonly<SideNavProps>) {
  const location = useLocation();
  const session = useSession();
  const showTemporaryDrawer = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));
  // Each entry below asks the user one question, and it is the same question `RouteAccessGuard`
  // asks of the route behind the link — so the nav cannot offer a page the guard will refuse
  // (issue #4651).
  const user = useSessionUser();

  const marketplaceMenuOpen = location.pathname.startsWith("/marketplace");
  // Open when the reader is in Attorney Hub's own knowledge base, unless they have said otherwise.
  // Held as an override rather than as the state itself: this nav is not remounted on navigation,
  // so seeding state from the route once left Support collapsed while one of its own pages was
  // open - which is what the Help button does. The override is still needed because Support also
  // contains a mailto, which has no route to match against.
  const [supportMenuChoice, setSupportMenuChoice] = useState<boolean | undefined>(undefined);
  const supportMenuOpen =
    supportMenuChoice ?? location.pathname.startsWith("/knowledge-base/attorney-hub");

  function handleNavClose(temporary: boolean) {
    if (temporary) {
      props.navMenuClosed();
    }
  }

  function drawer(temporary: boolean) {
    if (session.isRestrictedOnboarding) {
      return (
        <NavDrawer
          closeAfterTransition={false}
          temporary={temporary}
          variant={temporary ? "temporary" : "permanent"}
          anchor={temporary ? "right" : "left"}
          open={temporary ? props.showNav : true}
          onClose={() => {
            if (temporary) props.navMenuClosed();
          }}
          ModalProps={{ keepMounted: true }}
        >
          <NavContainer temporary={temporary}>
            <List>
              {user?.canAccessProposals() && (
                <ListItemButton
                  to="/proposals"
                  selected={location.pathname.startsWith("/proposals")}
                  component={Link}
                  onClick={() => handleNavClose(temporary)}
                >
                  <ListItemIcon><AssignmentIcon /></ListItemIcon>
                  <ListItemText primary="Proposals" />
                </ListItemButton>
              )}
              <ListItemButton
                to="/help"
                selected={location.pathname.startsWith("/help")}
                component={Link}
                onClick={() => handleNavClose(temporary)}
              >
                <ListItemIcon><ContactSupportIcon /></ListItemIcon>
                <ListItemText primary="Help" />
              </ListItemButton>
              <ListItemButton href="mailto:support@attorneyhub.com" target="_top" component="a">
                <ListItemIcon><ContactSupportIcon /></ListItemIcon>
                <ListItemText primary="Support" />
              </ListItemButton>
            </List>
          </NavContainer>
        </NavDrawer>
      );
    }

    return (
      <NavDrawer
        closeAfterTransition={false}
        temporary={temporary}
        variant={temporary ? "temporary" : "permanent"}
        anchor={temporary ? "right" : "left"}
        open={temporary ? props.showNav : true}
        onClose={() => {
          if (temporary) {
            props.navMenuClosed();
          }
        }}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
      >
        <NavContainer temporary={temporary}>
          <List>
            <ListItemButton
              to="/dashboard"
              selected={location.pathname === "/dashboard"}
              component={Link}
              onClick={() => handleNavClose(temporary)}
            >
              <ListItemIcon>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItemButton>
            {user?.canAccessCommunications() && (
              <ListItemButton
                to="/communications"
                selected={location.pathname.startsWith("/communications")}
                component={Link}
                onClick={() => handleNavClose(temporary)}
              >
                <ListItemIcon>
                  <InboxIcon />
                </ListItemIcon>
                <ListItemText primary="Communications" />
              </ListItemButton>
            )}
            {user?.canAccessWork() && (
              <ListItemButton
                to="/work"
                selected={location.pathname.startsWith("/work")}
                component={Link}
                onClick={() => handleNavClose(temporary)}
              >
                <ListItemIcon>
                  <WorkIcon />
                </ListItemIcon>
                <ListItemText primary="Work" />
              </ListItemButton>
            )}
            {user?.canAccessProposals() && (
              <ListItemButton
                to="/proposals"
                selected={location.pathname.startsWith("/proposals")}
                component={Link}
                onClick={() => handleNavClose(temporary)}
              >
                <ListItemIcon>
                  <AssignmentIcon />
                </ListItemIcon>
                <ListItemText primary="Proposals" />
              </ListItemButton>
            )}
            {user?.canAccessTeams() && (
              <ListItemButton
                to="/teams"
                selected={location.pathname.startsWith("/teams")}
                component={Link}
                onClick={() => handleNavClose(temporary)}
              >
                <ListItemIcon>
                  <GroupIcon />
                </ListItemIcon>
                <ListItemText primary="Teams" />
              </ListItemButton>
            )}
            {user?.canAccessFeeSchedules() && (
              <ListItemButton
                to="/fee-schedules"
                selected={location.pathname.startsWith("/fee-schedules")}
                component={Link}
                onClick={() => handleNavClose(temporary)}
              >
                <ListItemIcon>
                  <AccountBalanceIcon />
                </ListItemIcon>
                <ListItemText primary="Fee Schedules" />
              </ListItemButton>
            )}
            {user?.canAccessContracts() && (
              <ListItemButton
                to="/contracts"
                selected={location.pathname.startsWith("/contracts")}
                component={Link}
                onClick={() => handleNavClose(temporary)}
              >
                <ListItemIcon>
                  <FileCopy />
                </ListItemIcon>
                <ListItemText primary="Contracts" />
              </ListItemButton>
            )}
            {user?.canAccessKnowledgeBase() && (
              // Plain "Knowledge Base": this is the signed-in entity's own, and the reader knows
              // which entity they are signed in as. Naming it after the entity read as a near
              // duplicate of the entry under Support, which is now "Support Knowledge Base" and
              // named for what it is for rather than for who owns it.
              <ListItemButton
                to="/knowledge-base"
                selected={
                  location.pathname.startsWith("/knowledge-base")
                  && !location.pathname.startsWith("/knowledge-base/attorney-hub")
                }
                component={Link}
                onClick={() => handleNavClose(temporary)}
              >
                <ListItemIcon>
                  <MenuBookIcon />
                </ListItemIcon>
                <ListItemText primary="Knowledge Base" />
              </ListItemButton>
            )}
            {user?.canAccessMarketplace() && (
              <>
                <ListItemButton
                  to="/marketplace"
                  selected={location.pathname.startsWith("/marketplace")}
                  component={Link}
                  onClick={() => handleNavClose(temporary)}
                >
                  <ListItemIcon>
                    <StorefrontIcon />
                  </ListItemIcon>
                  <ListItemText primary="Marketplace" />
                  {marketplaceMenuOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse
                  in={marketplaceMenuOpen}
                  timeout="auto"
                  unmountOnExit
                  sx={{ backgroundColor: "#FAFAFA", pl: 2 }}
                >
                  <List component="div" disablePadding>
                    {user?.canSearchMarketplace() && (
                      <ListItemButton
                        to="/marketplace"
                        selected={new RegExp(/\/marketplace[^/]*$/).exec(location.pathname) !== null}
                        component={Link}
                        onClick={() => handleNavClose(temporary)}
                      >
                        <ListItemIcon>
                          <SearchIcon />
                        </ListItemIcon>
                        <ListItemText primary="Search Listings" />
                      </ListItemButton>
                    )}
                    <ListItemButton
                      to="/marketplace/bookmarks"
                      selected={location.pathname.startsWith("/marketplace/bookmarks")}
                      component={Link}
                      onClick={() => handleNavClose(temporary)}
                    >
                      <ListItemIcon>
                        <BookmarksOutlinedIcon />
                      </ListItemIcon>
                      <ListItemText primary="Bookmarks" />
                    </ListItemButton>
                    <ListItemButton
                      to="/marketplace/profiles"
                      selected={location.pathname.startsWith("/marketplace/profiles")}
                      component={Link}
                      onClick={() => handleNavClose(temporary)}
                    >
                      <ListItemIcon>
                        <SettingsIcon />
                      </ListItemIcon>
                      <ListItemText primary="Your Profiles" />
                    </ListItemButton>
                  </List>
                </Collapse>
              </>
            )}
            {user?.canViewFirmOnboardingStatus() && (
              <ListItemButton
                to="/firm-onboarding"
                selected={isRouteWithinRoot(location.pathname, "firm-onboarding")}
                component={Link}
                onClick={() => handleNavClose(temporary)}
              >
                <ListItemIcon>
                  <TimelineIcon />
                </ListItemIcon>
                <ListItemText primary="Firm Onboarding" />
              </ListItemButton>
            )}
            {user?.canViewClientProposalTemplates() && (
              <ListItemButton
                to="/client-proposal-templates"
                selected={isRouteWithinRoot(location.pathname, "client-proposal-templates")}
                component={Link}
                onClick={() => handleNavClose(temporary)}
              >
                <ListItemIcon>
                  <DescriptionOutlinedIcon />
                </ListItemIcon>
                <ListItemText primary="Proposal Templates" />
              </ListItemButton>
            )}
            {user?.canAccessNetwork() && (
              <ListItemButton
                to="/network"
                selected={isRouteWithinRoot(location.pathname, "network")}
                component={Link}
                onClick={() => handleNavClose(temporary)}
              >
                <ListItemIcon>
                  <AccountTreeIcon />
                </ListItemIcon>
                <ListItemText primary="Network" />
              </ListItemButton>
            )}
            <ListItemButton
              to={calendarRoute}
              selected={location.pathname.startsWith(calendarRoute)}
              component={Link}
              onClick={() => handleNavClose(temporary)}
            >
              <ListItemIcon>
                <TodayIcon />
              </ListItemIcon>
              <ListItemText primary="Calendar" />
            </ListItemButton>
            {user?.canAccessAdministration() && (
              <>
                <ListItemButton
                  to="/organization"
                  selected={location.pathname.startsWith("/organization")}
                  component={Link}
                  onClick={() => handleNavClose(temporary)}
                >
                  <ListItemIcon>
                    <AdminPanelSettingsIcon />
                  </ListItemIcon>
                  <ListItemText primary="Organization" />
                </ListItemButton>
                <ListItemButton
                  to="/permissions"
                  selected={location.pathname.startsWith("/permissions")}
                  component={Link}
                  onClick={() => handleNavClose(temporary)}
                >
                  <ListItemIcon>
                    <SecurityIcon />
                  </ListItemIcon>
                  <ListItemText primary="Permissions" />
                </ListItemButton>
              </>
            )}
            {user?.canAdministerPlatform() && (
              <ListItemButton
                to="/super-user"
                selected={location.pathname.startsWith("/super-user")}
                component={Link}
                onClick={() => handleNavClose(temporary)}
              >
                <ListItemIcon>
                  <SettingsAccessibility />
                </ListItemIcon>
                <ListItemText primary="Superuser Dashboard" />
              </ListItemButton>
            )}
            <ListItemButton
              onClick={() => setSupportMenuChoice(!supportMenuOpen)}
              selected={location.pathname.startsWith("/knowledge-base/attorney-hub")}
            >
              <ListItemIcon>
                <ContactSupportIcon />
              </ListItemIcon>
              <ListItemText primary="Support" />
              {supportMenuOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse
              in={supportMenuOpen}
              timeout="auto"
              unmountOnExit
              sx={{ backgroundColor: "#FAFAFA", pl: 2 }}
            >
              <List component="div" disablePadding>
                <ListItemButton
                  to="/knowledge-base/attorney-hub"
                  selected={location.pathname.startsWith("/knowledge-base/attorney-hub")}
                  component={Link}
                  onClick={() => handleNavClose(temporary)}
                >
                  <ListItemIcon>
                    <MenuBookIcon />
                  </ListItemIcon>
                  <ListItemText primary="Support Knowledge Base" />
                </ListItemButton>
                {/*
                  Kept as a direct mailto. Support has always been one click to an addressed email,
                  and routing it through a page first would cost that. The knowledge base article
                  above explains what to put in the message; this stays the way to send it.
                */}
                <ListItemButton href="mailto:support@attorneyhub.com" target="_top" component="a">
                  <ListItemIcon>
                    <EmailIcon />
                  </ListItemIcon>
                  <ListItemText primary="Email Support" />
                </ListItemButton>
              </List>
            </Collapse>
          </List>
        </NavContainer>
        {user?.canAccessMarketplace() && (
          <MarketplaceLink to="/marketplace">
            <img src={attorneyHubMarketplaceLogo} alt="Attorney Hub Marketplace" />
          </MarketplaceLink>
        )}
      </NavDrawer>
    );
  }

  return <React.Fragment>{drawer(showTemporaryDrawer)}</React.Fragment>;
}
